/**
 * Merging the three keybinding sources into one ordered table (架构方案 §5.9).
 *
 * The inputs are the platform's built-in table, what modules declared, and what
 * the user edited; the output is a single list the dispatcher resolves against
 * and the settings page renders. Keeping the merge here — rather than letting
 * each face re-derive it — is what stops the shortcuts page from disagreeing
 * with what the keyboard actually does.
 */

import type {
  GlobalShortcutStatus,
  KeybindingOverride,
  MainWindowShortcut,
  ResolvedGlobalBinding,
  ResolvedKeybinding,
} from '@hibro/shared';
import { KEYBINDING_SOURCE_RANK, MAIN_SHORTCUT_COMMAND } from '@hibro/shared';

import type { Chord, ParseChordOptions } from './chord';
import {
  chordMatches,
  isBrowserReservedChord,
  parseChord,
  toAccelerator,
} from './chord';

/** A binding paired with its compiled chord and why it may be unusable. */
export interface CompiledKeybinding {
  readonly binding: ResolvedKeybinding;
  /** Absent when {@link problem} says the chord could not be compiled. */
  readonly chord?: Chord;
  readonly problem?: KeybindingProblem;
}

/**
 * Why a declared binding does not currently work. Surfaced in the shortcuts page
 * and logged once at resolve time — never swallowed, because every one of these
 * looks identical from the user's chair ("I press it and nothing happens").
 */
export type KeybindingProblem =
  /** The chord names no key this platform can match (a manifest typo). */
  | 'unparseable'
  /** The browser keeps this chord for itself (web only, 见 18). */
  | 'browser-reserved'
  /** `scope: 'global'` but the chord has no Electron accelerator spelling. */
  | 'not-global-capable';

export interface ResolveKeybindingsInput {
  /** The platform's own defaults. */
  readonly builtin: readonly ResolvedKeybinding[];
  /** Contributed by modules, from `listContributions`. */
  readonly contributed: readonly ResolvedKeybinding[];
  readonly overrides: readonly KeybindingOverride[];
  /** True on the browser runtime, where reserved chords cannot be claimed. */
  readonly web?: boolean;
  readonly chordOptions?: ParseChordOptions;
}

/**
 * Merge the sources, apply the user's edits, and compile every chord.
 *
 * Ordered by ascending precedence (`builtin`, then `module`, then `user`), which
 * is the order the dispatcher scans backwards through — the same "later rules
 * win" shape as `keybindings.json` being appended below the defaults.
 */
export function resolveKeybindings(
  input: ResolveKeybindingsInput,
): CompiledKeybinding[] {
  const overrideById = new Map(
    input.overrides.map((override) => [override.id, override]),
  );
  const declared = [...input.builtin, ...input.contributed];
  const resolved: ResolvedKeybinding[] = [];

  for (const binding of declared) {
    const override = overrideById.get(binding.id);
    if (override?.disabled === true) {
      // The user removed this default outright; it resolves to nothing, and no
      // lower-precedence rule takes its place (the same as `-command`).
      continue;
    }
    if (override?.key !== undefined) {
      resolved.push({ ...binding, key: override.key, source: 'user' });
      continue;
    }
    resolved.push(binding);
  }

  resolved.sort(
    (a, b) =>
      KEYBINDING_SOURCE_RANK[a.source] - KEYBINDING_SOURCE_RANK[b.source],
  );

  return resolved.map((binding) => compile(binding, input));
}

function compile(
  binding: ResolvedKeybinding,
  input: ResolveKeybindingsInput,
): CompiledKeybinding {
  const chord = parseChord(binding.key, input.chordOptions);
  if (!chord) {
    return { binding, problem: 'unparseable' };
  }
  if (input.web === true && isBrowserReservedChord(chord, input.chordOptions)) {
    return { binding, chord, problem: 'browser-reserved' };
  }
  if (binding.scope === 'global' && toAccelerator(chord) === undefined) {
    return { binding, chord, problem: 'not-global-capable' };
  }
  return { binding, chord };
}

/**
 * Bindings this window's `keydown` listener acts on.
 *
 * Excludes both chords someone else executes: the OS-level grabs, and the
 * handful the main process runs off `before-input-event`. The second exclusion
 * matters — main claims those keys before the page sees them, so a renderer
 * entry for the same chord could only ever fire when main's table was missing,
 * dispatching a command no renderer implements.
 */
export function windowBindings(
  compiled: readonly CompiledKeybinding[],
): CompiledKeybinding[] {
  const mainOwned = new Set<string>(Object.values(MAIN_SHORTCUT_COMMAND));
  return compiled.filter(
    (entry) =>
      entry.problem === undefined &&
      entry.chord !== undefined &&
      entry.binding.scope !== 'global' &&
      !mainOwned.has(entry.binding.command),
  );
}

/**
 * Bindings to hand to the OS layer, already carrying the accelerator.
 *
 * Resolved here rather than in the main process because the merge with user
 * overrides happens here: two places deciding which chord is in effect is how a
 * rebind ends up working in-window and not globally.
 */
export function globalBindings(
  compiled: readonly CompiledKeybinding[],
): ResolvedGlobalBinding[] {
  const bindings: ResolvedGlobalBinding[] = [];
  for (const entry of compiled) {
    if (entry.binding.scope !== 'global' || !entry.chord) {
      continue;
    }
    const accelerator = toAccelerator(entry.chord);
    if (accelerator === undefined) {
      continue;
    }
    bindings.push({
      id: entry.binding.id,
      accelerator,
      command: entry.binding.command,
      ...(entry.binding.args ? { args: entry.binding.args } : {}),
    });
  }
  return bindings;
}

/**
 * Window chords the main process executes itself (F11, the dev reload pair).
 *
 * They travel down with the global ones so a rebind reaches both executors at
 * once. Main sees the key before the page does, which is the whole reason these
 * three did not move into the renderer: the chord has to keep working while a
 * `WebContentsView` or a cross-origin iframe holds focus.
 */
export function mainWindowShortcuts(
  compiled: readonly CompiledKeybinding[],
): MainWindowShortcut[] {
  const owned = new Set<string>(Object.values(MAIN_SHORTCUT_COMMAND));
  const shortcuts: MainWindowShortcut[] = [];
  for (const entry of compiled) {
    if (
      !entry.chord ||
      entry.problem !== undefined ||
      entry.binding.scope === 'global' ||
      !owned.has(entry.binding.command)
    ) {
      continue;
    }
    shortcuts.push({
      id: entry.binding.id,
      command: entry.binding.command,
      chord: entry.chord,
    });
  }
  return shortcuts;
}

/** Two or more bindings competing for the same chord under the same condition. */
export interface KeybindingConflict {
  readonly chord: Chord;
  /** Ascending precedence, so the last entry is the one that actually fires. */
  readonly bindings: readonly ResolvedKeybinding[];
}

/**
 * Find chords that resolve to more than one command.
 *
 * Only bindings sharing the *same* `when` are a conflict: differing conditions
 * is precisely how one chord serves several contexts, and flagging that would
 * make the warning meaningless. Same-condition duplicates are real — one of them
 * can never fire — so the settings page shows them and the loser is named.
 */
export function findKeybindingConflicts(
  compiled: readonly CompiledKeybinding[],
): KeybindingConflict[] {
  const groups = new Map<string, CompiledKeybinding[]>();
  for (const entry of compiled) {
    if (!entry.chord || entry.problem !== undefined) {
      continue;
    }
    const key = `${chordKey(entry.chord)}|${entry.binding.when ?? ''}|${
      entry.binding.scope ?? 'window'
    }`;
    groups.set(key, [...(groups.get(key) ?? []), entry]);
  }

  const conflicts: KeybindingConflict[] = [];
  for (const entries of groups.values()) {
    const commands = new Set(entries.map((entry) => entry.binding.command));
    if (entries.length < 2 || commands.size < 2) {
      continue;
    }
    conflicts.push({
      chord: entries[0].chord as Chord,
      bindings: entries.map((entry) => entry.binding),
    });
  }
  return conflicts;
}

function chordKey(chord: Chord): string {
  return [
    chord.ctrl ? 'c' : '',
    chord.shift ? 's' : '',
    chord.alt ? 'a' : '',
    chord.meta ? 'm' : '',
    chord.code,
  ].join('');
}

/**
 * Whether `chord` is already claimed by something other than `excludeId`, for
 * the recorder to warn on before a rebind is saved.
 */
export function chordClaimedBy(
  compiled: readonly CompiledKeybinding[],
  chord: Chord,
  excludeId: string,
): ResolvedKeybinding[] {
  return compiled
    .filter(
      (entry) =>
        entry.chord !== undefined &&
        entry.binding.id !== excludeId &&
        chordMatches(entry.chord, chord),
    )
    .map((entry) => entry.binding);
}

/** Index the OS registration outcomes by binding id, for the settings page. */
export function globalStatusById(
  statuses: readonly GlobalShortcutStatus[],
): Map<string, GlobalShortcutStatus> {
  return new Map(statuses.map((status) => [status.id, status]));
}
